import { useEffect, useState, type Dispatch, type SetStateAction } from 'react';
import { useLocation } from 'react-router';
import { loadDraft, persistDraft } from './draft-storage';

/**
 * FRONT-1036: drop-in replacement for `useState('')` on the composer's draft
 * text. Hydrates from the persisted draft for this key on mount/key change,
 * and persists on every change — clearing falls out for free whenever the
 * caller's own reset logic (send, restore, manual delete) sets the text back
 * to empty, so callers never need their own explicit clear call.
 *
 * A `draftKey` change can happen without a remount — e.g. an inbox item's
 * key starts as `inbox-<id>` and is promoted to a real session id on its
 * first send, still inside the same `ChatInput` instance. Hydrating via a
 * `useEffect` (the first cut of this fix) reacted one render too late: the
 * persist effect would still see that render's stale `text` (typed for the
 * OLD key) and briefly write it under the NEW key before a follow-up render
 * corrected it — a real cross-key contamination window, not just a visual
 * flash (Aikido review finding on PR #677). Adjusting `text` synchronously
 * during render instead — React's documented pattern for resetting state
 * when a value changes without an extra render — means `text` is never
 * stale relative to `draftKey` at any point the persist effect can observe.
 */
export function useDraftText(
  draftKey: string | undefined,
): [string, Dispatch<SetStateAction<string>>] {
  const location = useLocation();
  // An incoming nav-draft prefill (chat-input.tsx's own location.state
  // effect, which fires after this render) always wins over a persisted
  // draft — deferred to by leaving `text` empty here.
  const navDraftPending = !!(location.state as { draft?: string } | null)?.draft;

  const [text, setText] = useState(() => (navDraftPending ? '' : loadDraft(draftKey)));
  const [loadedKey, setLoadedKey] = useState(draftKey);

  if (draftKey !== loadedKey) {
    setLoadedKey(draftKey);
    setText(navDraftPending ? '' : loadDraft(draftKey));
  }

  useEffect(() => {
    persistDraft(draftKey, text);
  }, [draftKey, text]);

  return [text, setText];
}
